import { PastCallsType } from './calls.service'
import { CallFilters } from './filters/filters.component'

const me = 'me'
const all = 'all'

export const queryKeys = {
  calls: {
    base: ['calls'],

    support: {
      all: ['support', 'calls', 'all'],
      completed: ['support', 'calls', 'completed'],
      today: ['support', 'calls', 'today'],
      byId: (id: number) => ['support', 'calls', id],
      byUserId: (userId: number) => ['support', 'calls', 'user', userId],
      byOrganizationId: (organizationId: number) => ['support', 'calls', 'organization', organizationId],
      debugData: (id: number) => ['support', 'calls', id, 'debug-data'],
      hallucinationAudit: (callId: number, jobId: number) => ['support', 'calls', callId, 'hallucination-audit', jobId],
      editorDebug: (id: number) => ['support', 'calls', id, 'editor-debug'],
      agendaTranscriptMappings: ['support', 'calls', 'agenda-transcript-mappings'],
      agendaTranscriptMapping: (callId: number, jobId: number) => ['support', 'calls', callId, 'agenda-transcript-mapping', jobId],
    },

    client: {
      base: ['client-calls'],
      list: (params: object) => ['client-calls', 'list', params],
      retentionExtensions: (callId: number) => ['client-calls', callId, 'retention-extensions'],
    },

    retentionExtensionRequests: {
      base: ['calls', 'retention-extension-requests'],
      options: (callId: number) => ['calls', 'retention-extension-requests', callId, 'options'],
      byId: (callId: number, requestId: number) => ['calls', 'retention-extension-requests', callId, requestId],
    },

    byId: (id: number) => ['calls', id],
    list: {
      base: ['call-list'],
      upcoming: ['call-list', 'upcoming'],
      ongoing: ['call-list', 'ongoing'],
      past: {
        base: ['call-list', 'past'],
        byType: (type: PastCallsType) => ['call-list', 'past', type],
      },
    },
    accessRequests: {
      base: ['calls', 'access-requests'],
      byId: (id: number) => ['calls', 'access-requests', id],
    },
  },

  users: {
    base: ['users'],
    byId: (id: number) => ['users', id],
    byOrganizationId: (organizationId: number) => ['users', 'organization', organizationId],
    me: ['users', me],
    calendarSyncStatus: ['users', me, 'calendar-sync-status'],
    e2eTestUserOfflineToken: ['users', 'e2e-test-user', 'offline-token'],
    settings: ['user-settings'],
    search: (query: string) => ['users', 'search', query],
  },

  editorPollingFallback: {
    base: ['admin', 'editor-polling-fallback'],
  },

  demoProfiles: {
    base: ['demo-profiles'],
    byId: (id: number) => ['demo-profiles', id],
  },

  intercom: {
    base: ['intercom'],
    admins: ['intercom', 'admins'],
  },

  supportConversations: {
    base: ['support-conversations'],
    list: (filters: unknown) => ['support-conversations', 'list', filters],
    waitingCount: ['support-conversations', 'waiting-count'],
    viewer: ['support-conversations', 'viewer'],
    knowledgeGaps: ['support-conversations', 'knowledge-gaps'],
    detail: (intercomId: string) => ['support-conversations', 'detail', intercomId],
    context: (intercomId: string) => ['support-conversations', 'context', intercomId],
  },

  search: {
    results: {
      byFilters: (filters: CallFilters) => ['search-results', filters],
    },
    attendees: ['search-attendees'],
    speakers: ['search-speakers'],
    guestOrganizations: ['search-guest-organizations'],
  },

  clients: {
    base: ['clients'],
    all: ['clients', all],
    me: ['clients', me],
    users: ['clients', me, 'users'],
    byId: (id: number) => ['clients', id],
  },

  organizations: {
    base: ['organizations'],
    byId: (id: number) => ['organizations', id],
    // Nested under byId on purpose: the support view returns a different shape, but invalidating
    // byId should still refresh it.
    supportById: (id: number) => ['organizations', id, 'support'],
    me: ['organizations', me],
    all: ['organizations', all],
    users: ['organization-users'],
    settings: ['organization-settings'],
  },

  guestOrganizations: {
    base: ['guest-organizations'],
    all: ['all-guest-organizations'],
  },
  callProfiles: {
    base: ['call-profiles'],
    all: ['call-profiles', all],
    own: ['call-profiles', 'own'],
    byId: (id: number) => ['call-profiles', id],
  },

  templates: {
    base: ['templates'],
    all: ['templates', all],
    byCallId: (callId: number) => ['templates', 'by-call-id', callId],
    byId: (id: number) => ['templates', id],
    // The own-organization and support lists return different scopes, so they need separate cache
    // entries. They stay under `base` so invalidating it still refreshes every model list.
    models: {
      base: ['templates', 'models'],
      own: ['templates', 'models', 'own'],
      support: ['templates', 'models', 'support'],
      byOrganization: (organizationId: number) => ['templates', 'models', organizationId],
    },
  },

  experiments: {
    base: ['experiments'],
    all: ['experiments', 'all'],
    byId: (id: number) => ['experiments', id],
  },

  llmDefaults: {
    base: ['llm-defaults'],
    all: ['llm-defaults', all],
    jobTasks: ['llm-defaults', 'job-tasks'],
  },

  evaluators: {
    base: ['evaluators'],
    all: ['evaluators', all],
    byId: (id: number) => ['evaluators', id],
  },

  evalDatasets: {
    base: ['eval-datasets'],
    all: ['eval-datasets', all],
    runs: (id: number) => ['eval-datasets', id, 'runs'],
    runResults: (id: number, runId: number) => ['eval-datasets', id, 'runs', runId, 'results'],
  },

  llmTracing: {
    base: ['llm-tracing'],
    skeletonByJob: (callBatchJobRunId: number) => ['llm-tracing', 'skeleton', callBatchJobRunId],
    systemPromptsByJob: (callBatchJobRunId: number) => ['llm-tracing', 'system-prompts', callBatchJobRunId],
    spanById: (spanId: string) => ['llm-tracing', 'span', spanId],
  },

  metrics: {
    // These take the request parameters so that each filter combination gets its own cache entry.
    // Sharing one entry across combinations makes a changed filter resolve to the previous
    // combination's data, which is then rendered as if it were current until the refetch lands.
    list: (params: object) => ['metrics', 'list', params],
    completedCalls: (params: object) => ['metrics', 'completed-calls', params],
    featureAdoptionByUser: (organizationId: number) => ['metrics', 'feature-adoption-by-user', organizationId],
    engagementByUser: (organizationId: number) => ['metrics', 'engagement-by-user', organizationId],
    usageByUser: (type: string, params: object) => ['metrics', 'usage-by-user', type, params],
    usageByOrganization: (params: object) => ['metrics', 'usage-by-organization', params],
    meetingRoomMetrics: (organizationIds: number[]) => ['metrics', 'meeting-room-metrics', ...organizationIds],
    sla: (organizationIds: number[], month: string) => ['metrics', 'sla', month, ...organizationIds],
    performance: (organizationId: number | null, startDate: string | null, endDate: string | null) => [
      'metrics',
      'performance',
      organizationId,
      startDate,
      endDate,
    ],
    activity: (organizationId: number | null, startDate: string, endDate: string, precision: string) => [
      'metrics',
      'activity',
      organizationId,
      startDate,
      endDate,
      precision,
    ],
    // The list itself is keyed by the table's own source; this is the prefix it caches under, so that
    // the rows and the facet option lists share one entry.
    activityLog: () => ['metrics', 'activity-log'],
    activityLogCoverage: () => ['metrics', 'activity-log', 'coverage'],
    // Takes no parameters: the endpoint answers for the caller's whole client and its own period.
    subscriptionUsage: () => ['metrics', 'subscription-usage'],
  },
}
